import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../services/api";
import { format } from "date-fns";
import { useToast } from "../../context/ToastContext";

const woStatusBadge = (status) => {
  const map = {
    "Pending":                "ims-badge-pending",
    "Approved by Principal":  "ims-badge-principal",
    "Approved by Management": "ims-badge-approved",
    "Partially Completed":    "ims-badge-partial",
    "Completed":              "ims-badge-full",
    "Rejected by Principal":  "ims-badge-rejected",
    "Rejected by Management": "ims-badge-rejected",
  };
  return <span className={`ims-badge ${map[status] || "ims-badge-notcreated"}`}>{status}</span>;
};

const ViewWorkOrder = () => {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { showToast } = useToast();

  const [order,       setOrder]       = useState(null);
  const [items,       setItems]       = useState([]);
  const [completions, setCompletions] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Rejection modal
  const [showRejectModal, setShowRejectModal]     = useState(false);
  const [rejectionReason, setRejectionReason]     = useState("");
  const [pendingRejectStatus, setPendingRejectStatus] = useState("");

  // Completion modal
  const [showCompModal,   setShowCompModal]   = useState(false);
  const [compDate,        setCompDate]        = useState(new Date().toISOString().slice(0, 10));
  const [compType,        setCompType]        = useState("Full");
  const [compRemarks,     setCompRemarks]     = useState("");

  const user = JSON.parse(localStorage.getItem("user"));

  const fetchWO = async () => {
    try {
      const res = await axios.get(`/work-orders/${id}`);
      setOrder(res.data.order);
      setItems(res.data.items);
      setCompletions(res.data.completions || []);
    } catch (err) {
      console.error("Error fetching work order:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWO(); }, [id]);

  const handleStatusUpdate = async (newStatus, rejectReason = "") => {
    setActionLoading(true);
    try {
      await axios.put(`/work-orders/${id}/status`, {
        status:           newStatus,
        role:             user.role,
        user_id:          user.id,
        rejection_reason: rejectReason,
      });
      showToast("success", "Status Updated", `Work Order ${newStatus} successfully.`);
      fetchWO();
    } catch (err) {
      showToast("error", "Failed", "Could not update status.");
    } finally {
      setActionLoading(false);
      setShowRejectModal(false);
      setRejectionReason("");
    }
  };

  const openRejectModal = (status) => {
    setPendingRejectStatus(status);
    setRejectionReason("");
    setShowRejectModal(true);
  };

  const handleCompletion = async () => {
    if (!compDate) return alert("Please select a completion date.");
    setActionLoading(true);
    try {
      const res = await axios.post(`/work-orders/${id}/completion`, {
        completion_date: compDate,
        type:    compType,
        remarks: compRemarks || null,
      });
      showToast("success", "Completion Recorded", `WC# ${res.data.wc_number} — ${res.data.status}`);
      setShowCompModal(false);
      setCompRemarks("");
      fetchWO();
    } catch (err) {
      showToast("error", "Failed", err?.response?.data?.message || "Could not record completion.");
    } finally {
      setActionLoading(false);
    }
  };

  const generatePDF = async () => {
    try {
      const res  = await axios.get(`/work-orders/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch {
      showToast("error", "Failed", "Could not generate PDF.");
    }
  };

  const downloadAttachment = async () => {
    try {
      const res = await axios.get(`/work-orders/${id}/attachment`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = order.attachment_original_name || "Work_Order_Attachment.pdf";
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast("error", "Download Failed", "Could not download the Work Order attachment.");
    }
  };

  if (loading) return <div className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-primary"></i></div>;
  if (!order)  return <div className="alert alert-danger">Work Order not found.</div>;

  const canApproveRejectPrincipal  = user.role === "Principal" && order.status === "Pending";
  const canApproveRejectManagement = user.role === "Management" && order.status === "Approved by Principal";
  const canMarkCompletion          = user.role === "store_manager" &&
    ["Approved by Management", "Partially Completed"].includes(order.status);
  const canPrintPDF                = ["Approved by Management", "Partially Completed", "Completed"].includes(order.status);

  return (
    <div>
      {/* ── Page header ── */}
      <div className="ims-page-header">
        <div>
          <h4>Work Order — {order.wo_number}</h4>
          <p>Raised on {format(new Date(order.wo_date), "dd MMM yyyy")}</p>
        </div>
        <div className="d-flex gap-2">
          {order.attachment_path && (
            <button className="btn btn-sm btn-outline-danger" onClick={downloadAttachment}>
              <i className="fas fa-file-pdf mr-1"></i> Attachment
            </button>
          )}
          {canPrintPDF && (
            <button className="btn btn-sm btn-outline-primary" onClick={generatePDF}>
              <i className="fas fa-print mr-1"></i> Print PDF
            </button>
          )}
          <button className="btn btn-sm btn-secondary" onClick={() => navigate("/dashboard/work-orders")}>
            <i className="fas fa-arrow-left mr-1"></i> Back
          </button>
        </div>
      </div>

      {/* ── Details card ── */}
      <div className="card shadow-sm mb-4" style={{ border: "none", borderRadius: "12px" }}>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <table style={{ fontSize: "13px", width: "100%" }}>
                <tbody>
                  <tr><td style={{ color: "#64748b", width: "140px" }}>WO Number</td><td><strong>{order.wo_number}</strong></td></tr>
                  <tr><td style={{ color: "#64748b" }}>Subject</td><td><strong>{order.title}</strong></td></tr>
                  {order.location && <tr><td style={{ color: "#64748b" }}>Location</td><td>{order.location}</td></tr>}
                  {order.start_date && <tr><td style={{ color: "#64748b" }}>Start Date</td><td>{format(new Date(order.start_date), "dd MMM yyyy")}</td></tr>}
                  {order.end_date   && <tr><td style={{ color: "#64748b" }}>End Date</td><td>{format(new Date(order.end_date), "dd MMM yyyy")}</td></tr>}
                  <tr><td style={{ color: "#64748b" }}>Status</td><td>{woStatusBadge(order.status)}</td></tr>
                  {order.rejection_reason && (
                    <tr><td style={{ color: "#64748b" }}>Rejection Reason</td><td style={{ color: "#ef4444" }}>{order.rejection_reason}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="col-md-6">
              <table style={{ fontSize: "13px", width: "100%" }}>
                <tbody>
                  <tr><td style={{ color: "#64748b", width: "140px" }}>Contractor</td><td><strong>{order.vendor_name}</strong></td></tr>
                  {order.vendor_phone && <tr><td style={{ color: "#64748b" }}>Phone</td><td>{order.vendor_phone}</td></tr>}
                  {order.vendor_gst   && <tr><td style={{ color: "#64748b" }}>GST/PAN</td><td>{order.vendor_gst}</td></tr>}
                  <tr><td style={{ color: "#64748b" }}>Created By</td><td>{order.created_by_name}</td></tr>
                  {order.principal_name  && <tr><td style={{ color: "#64748b" }}>Principal</td><td>{order.principal_name}</td></tr>}
                  {order.management_name && <tr><td style={{ color: "#64748b" }}>Management</td><td>{order.management_name}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Scope of Work ── */}
      <div className="card shadow-sm mb-4" style={{ border: "none", borderRadius: "12px" }}>
        <div className="card-header" style={{ background: "#f8fafc", borderRadius: "12px 12px 0 0", borderBottom: "1px solid #f1f5f9" }}>
          <strong style={{ fontSize: "14px" }}><i className="fas fa-list-ul mr-2 text-primary"></i>Scope of Work</strong>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="ims-table">
              <thead>
                <tr>
                  <th>Sr</th>
                  <th>Description of Work</th>
                  <th>Unit</th>
                  <th>Qty</th>
                  <th>Rate (₹)</th>
                  <th>Disc%</th>
                  <th>Discount</th>
                  <th>GST%</th>
                  <th>GST</th>
                  <th>Final Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => (
                  <tr key={item.id}>
                    <td>{i + 1}</td>
                    <td><strong>{item.description}</strong></td>
                    <td>{item.unit}</td>
                    <td>{parseFloat(item.qty).toFixed(2)}</td>
                    <td>₹{parseFloat(item.rate).toFixed(2)}</td>
                    <td>{parseFloat(item.discount_percent).toFixed(2)}%</td>
                    <td>₹{parseFloat(item.discount_total).toFixed(2)}</td>
                    <td>{parseFloat(item.gst_percent).toFixed(2)}%</td>
                    <td>₹{parseFloat(item.gst_total).toFixed(2)}</td>
                    <td><strong>₹{parseFloat(item.final_total).toFixed(2)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Totals */}
          <div className="d-flex justify-content-end p-3">
            <table style={{ fontSize: "13px", minWidth: "260px" }}>
              <tbody>
                <tr><td style={{ color: "#64748b", paddingRight: "20px" }}>Subtotal</td><td className="text-right">₹{parseFloat(order.subtotal || 0).toFixed(2)}</td></tr>
                <tr><td style={{ color: "#64748b" }}>Discount</td><td className="text-right">₹{parseFloat(order.discount_total || 0).toFixed(2)}</td></tr>
                <tr><td style={{ color: "#64748b" }}>GST</td><td className="text-right">₹{parseFloat(order.gst_total || 0).toFixed(2)}</td></tr>
                <tr style={{ borderTop: "2px solid #e2e8f0" }}>
                  <td><strong>Grand Total</strong></td>
                  <td className="text-right"><strong style={{ fontSize: "16px", color: "#0f172a" }}>₹{parseFloat(order.grand_total || 0).toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Completion History ── */}
      {completions.length > 0 && (
        <div className="card shadow-sm mb-4" style={{ border: "none", borderRadius: "12px" }}>
          <div className="card-header" style={{ background: "#f8fafc", borderRadius: "12px 12px 0 0", borderBottom: "1px solid #f1f5f9" }}>
            <strong style={{ fontSize: "14px" }}><i className="fas fa-check-double mr-2 text-success"></i>Completion History</strong>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="ims-table">
                <thead>
                  <tr><th>WC Number</th><th>Date</th><th>Type</th><th>Remarks</th><th>Recorded By</th></tr>
                </thead>
                <tbody>
                  {completions.map(c => (
                    <tr key={c.id}>
                      <td><strong>{c.wc_number}</strong></td>
                      <td>{format(new Date(c.completion_date), "dd MMM yyyy")}</td>
                      <td>
                        <span className={`ims-badge ${c.type === "Full" ? "ims-badge-full" : "ims-badge-partial"}`}>
                          {c.type}
                        </span>
                      </td>
                      <td>{c.remarks || <span className="text-muted">—</span>}</td>
                      <td>{c.completed_by_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Action Buttons ── */}
      <div className="d-flex gap-2 flex-wrap mb-4">

        {/* Principal actions */}
        {canApproveRejectPrincipal && (
          <>
            <button
              className="btn btn-success btn-sm"
              disabled={actionLoading}
              onClick={() => handleStatusUpdate("Approved by Principal")}
            >
              <i className="fas fa-check mr-1"></i> Approve
            </button>
            <button
              className="btn btn-danger btn-sm"
              disabled={actionLoading}
              onClick={() => openRejectModal("Rejected by Principal")}
            >
              <i className="fas fa-times mr-1"></i> Reject
            </button>
          </>
        )}

        {/* Management actions */}
        {canApproveRejectManagement && (
          <>
            <button
              className="btn btn-success btn-sm"
              disabled={actionLoading}
              onClick={() => handleStatusUpdate("Approved by Management")}
            >
              <i className="fas fa-check-double mr-1"></i> Final Approve
            </button>
            <button
              className="btn btn-danger btn-sm"
              disabled={actionLoading}
              onClick={() => openRejectModal("Rejected by Management")}
            >
              <i className="fas fa-times mr-1"></i> Reject
            </button>
          </>
        )}

        {/* Mark Completion */}
        {canMarkCompletion && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => { setCompType("Full"); setCompRemarks(""); setShowCompModal(true); }}
          >
            <i className="fas fa-clipboard-check mr-1"></i>
            {order.status === "Partially Completed" ? "Mark Final Completion" : "Mark Completion"}
          </button>
        )}
      </div>

      {/* ── Reject Modal ── */}
      {showRejectModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog">
            <div className="modal-content shadow-lg" style={{ borderRadius: "12px", border: "none" }}>
              <div className="modal-header" style={{ borderRadius: "12px 12px 0 0" }}>
                <h5 className="modal-title" style={{ fontWeight: 700, color: "#991b1b" }}>
                  <i className="fas fa-times-circle mr-2"></i>Reject Work Order
                </h5>
                <button className="btn-close" onClick={() => setShowRejectModal(false)}></button>
              </div>
              <div className="modal-body">
                <label style={{ fontWeight: 600, fontSize: "13px" }}>Reason for Rejection <span className="text-muted">(optional)</span></label>
                <textarea
                  className="form-control form-control-sm mt-1"
                  rows="3"
                  value={rejectionReason}
                  onChange={e => setRejectionReason(e.target.value)}
                  placeholder="Provide a brief reason..."
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-sm btn-secondary" onClick={() => setShowRejectModal(false)}>Cancel</button>
                <button
                  className="btn btn-sm btn-danger"
                  disabled={actionLoading}
                  onClick={() => handleStatusUpdate(pendingRejectStatus, rejectionReason)}
                >
                  {actionLoading ? <i className="fas fa-spinner fa-spin mr-1"></i> : null}
                  Confirm Rejection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Completion Modal ── */}
      {showCompModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog">
            <div className="modal-content shadow-lg" style={{ borderRadius: "12px", border: "none" }}>
              <div className="modal-header" style={{ borderRadius: "12px 12px 0 0" }}>
                <h5 className="modal-title" style={{ fontWeight: 700, color: "#065f46" }}>
                  <i className="fas fa-clipboard-check mr-2"></i>Record Work Completion
                </h5>
                <button className="btn-close" onClick={() => setShowCompModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label style={{ fontWeight: 600, fontSize: "13px" }}>Completion Date <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className="form-control form-control-sm mt-1"
                    value={compDate}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={e => setCompDate(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label style={{ fontWeight: 600, fontSize: "13px" }}>Completion Type <span className="text-danger">*</span></label>
                  <div className="d-flex gap-3 mt-2">
                    {["Partial", "Full"].map(t => (
                      <label key={t} style={{ cursor: "pointer", fontWeight: compType === t ? 700 : 400 }}>
                        <input
                          type="radio"
                          name="compType"
                          value={t}
                          checked={compType === t}
                          onChange={() => setCompType(t)}
                          className="mr-1"
                        />
                        {t} Completion
                      </label>
                    ))}
                  </div>
                </div>
                <div className="mb-2">
                  <label style={{ fontWeight: 600, fontSize: "13px" }}>Remarks <span className="text-muted">(optional)</span></label>
                  <textarea
                    className="form-control form-control-sm mt-1"
                    rows="2"
                    value={compRemarks}
                    onChange={e => setCompRemarks(e.target.value)}
                    placeholder="Any notes about the work completion..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-sm btn-secondary" onClick={() => setShowCompModal(false)}>Cancel</button>
                <button
                  className="btn btn-sm btn-success"
                  disabled={actionLoading}
                  onClick={handleCompletion}
                >
                  {actionLoading ? <i className="fas fa-spinner fa-spin mr-1"></i> : <i className="fas fa-save mr-1"></i>}
                  Save Completion
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewWorkOrder;
